import React from 'react';
import { ImagePreview } from '@/components';
import { Maintenance, MaintenanceSeverity } from '@/types/common';
import {
  getMaintenanceReasonLabel,
  getMaintenanceCategoryLabel,
  getMaintenanceSeverityLabel,
  getCostResponsibilityLabel,
} from '@/utils/maintenanceEnums';

interface MaintenanceDetailContentProps {
  maintenance: Maintenance;
}

const displayValue = (value?: string | number | null) => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

const getSeverityBadge = (severity?: string) => {
  const baseClasses =
    'inline-flex items-center gap-2 rounded-lg border px-2 py-1 text-xs font-medium text-gray-700';

  return `${baseClasses} border-gray-200`;
};

const getSeverityDot = (severity?: string) => {
  switch (severity) {
    case MaintenanceSeverity.LOW:
      return 'bg-green-500';
    case MaintenanceSeverity.MEDIUM:
      return 'bg-blue-500';
    case MaintenanceSeverity.HIGH:
      return 'bg-orange-500';
    case MaintenanceSeverity.CRITICAL:
      return 'bg-red-500';
    default:
      return 'bg-gray-400';
  }
};

const formatDate = (value?: string) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-GB');
};

const formatCurrency = (value?: number) => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(value);
};

const MaintenanceDetailContent: React.FC<MaintenanceDetailContentProps> = ({
  maintenance,
}) => {
  const categories = Array.isArray(maintenance.categories)
    ? maintenance.categories
        .map((category) => getMaintenanceCategoryLabel(category as any))
        .join(', ')
    : '-';

  const costResponsibility = maintenance.costResponsibility || '';
  const costResponsibilityMeta: Record<
    string,
    { label: string; value?: string }
  > = {
    OEM: { label: 'OEM Name', value: maintenance.oemName },
    CLIENT: { label: 'Client Name', value: maintenance.clientName },
    MITRA: {
      label: 'Mitra Name',
      value:
        maintenance.driverCode && maintenance.driverName
          ? `${maintenance.driverCode} - ${maintenance.driverName}`
          : maintenance.driverName || maintenance.driverCode,
    },
  };

  const costResponsibilityDetail =
    costResponsibilityMeta[costResponsibility]?.value || '';
  const costResponsibilityLabel =
    costResponsibilityMeta[costResponsibility]?.label || '';

  return (
    <div className="w-full space-y-6 lg:flex-1">
      <div className="border border-gray-200 px-5 py-4 shadow-sm rounded-lg">
        <div className="mb-3 flex items-center gap-2 text-[16px] font-medium text-gray-800">
          <span className="ml-[-5px] inline-flex h-7 w-7 items-center justify-center text-gray-600">
            <i className="ri-motorbike-line text-base" />
          </span>
          Vehicle
        </div>

        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2">
            <div className="text-gray-500">Plate Number</div>
            <div className="text-right font-medium text-gray-900">
              {displayValue(maintenance.vehiclePlatNomor)}
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="text-gray-500">OEM</div>
            <div className="text-right font-medium text-gray-900">
              {displayValue(maintenance.vehicleOemName)}
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="text-gray-500">Model</div>
            <div className="text-right font-medium text-gray-900">
              {displayValue(maintenance.vehicleModelName)}
            </div>
          </div>
        </div>
      </div>

      <div className="border border-gray-200 px-5 py-4 shadow-sm rounded-lg">
        <div className="mb-3 flex items-center gap-2 text-[16px] font-medium text-gray-800">
          <span className="ml-[-5px] inline-flex h-7 w-7 items-center justify-center text-gray-600">
            <i className="ri-alert-line text-base" />
          </span>
          Maintenance Details
        </div>

        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2">
            <div className="text-gray-500">Reason</div>
            <div className="text-right font-medium text-gray-900">
              {maintenance.reason
                ? getMaintenanceReasonLabel(maintenance.reason as any)
                : '-'}
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="text-gray-500">Maintenance Details</div>
            <div className="text-right font-medium text-gray-900">
              {categories || '-'}
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="text-gray-500">Tanggal Maintenance</div>
            <div className="text-right font-medium text-gray-900">
              {formatDate(maintenance.maintenanceDate)}
            </div>
          </div>
          <div className="grid grid-cols-2 items-center">
            <div className="text-gray-500">Severity</div>
            <div className="text-right">
              <span className={getSeverityBadge(maintenance.severity)}>
                <span
                  className={`h-2 w-2 rounded-full ${getSeverityDot(
                    maintenance.severity
                  )}`}
                />
                {getMaintenanceSeverityLabel(
                  maintenance.severity as MaintenanceSeverity
                )}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="text-gray-500">OEM Service Center</div>
            <div className="text-right font-medium text-gray-900">
              {displayValue(maintenance.oemServiceCenter)}
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="text-gray-500">Expected Completion Date</div>
            <div className="text-right font-medium text-gray-900">
              {formatDate(maintenance.expectedCompletionDate)}
            </div>
          </div>
        </div>
      </div>

      <div className="border border-gray-200 px-5 py-4 shadow-sm rounded-lg">
        <div className="mb-3 flex items-center gap-2 text-[16px] font-medium text-gray-800">
          <span className="ml-[-5px] inline-flex h-7 w-7 items-center justify-center text-gray-600">
            <i className="ri-wallet-3-line text-base" />
          </span>
          Cost Details
        </div>

        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2">
            <div className="text-gray-500">Estimated Cost</div>
            <div className="text-right font-medium text-gray-900">
              {formatCurrency(maintenance.estimatedCost)}
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="text-gray-500">Biaya Ditanggung</div>
            <div className="text-right font-medium text-gray-900">
              {maintenance.costResponsibility
                ? getCostResponsibilityLabel(
                    maintenance.costResponsibility as any
                  )
                : '-'}
            </div>
          </div>
          {costResponsibility !== 'DASH' && costResponsibilityDetail && (
            <div className="grid grid-cols-2">
              <div className="text-gray-500">{costResponsibilityLabel}</div>
              <div className="text-right font-medium text-gray-900">
                {displayValue(costResponsibilityDetail)}
              </div>
            </div>
          )}
        </div>
      </div>

      <div>
        <div className="mb-2 text-[14px] font-medium text-gray-500">Photo</div>
        <div className="border border-gray-200 p-4 shadow-sm rounded-lg">
          {maintenance.vehicleImageURL ? (
            <ImagePreview
              src={maintenance.vehicleImageURL}
              alt="Maintenance"
              className="h-48 w-full rounded-md object-contain"
            />
          ) : (
            <div className="flex h-48 items-center justify-center rounded-md bg-gray-50 text-sm text-gray-400">
              No image available
            </div>
          )}
        </div>
      </div>

      <div>
        <div className="mb-2 text-[14px] font-medium text-gray-500">
          Description
        </div>
        <div className="border border-gray-200 p-4 shadow-sm rounded-lg">
          <p className="text-sm break-words leading-relaxed text-gray-700">
            {displayValue(maintenance.issueDescription)}
          </p>
        </div>
      </div>

      <div>
        <div className="mb-2 text-[14px] font-medium text-gray-500">Notes</div>
        <div className="border border-gray-200 p-4 shadow-sm rounded-lg">
          <p className="text-sm break-words leading-relaxed text-gray-700">
            {displayValue(maintenance.notes)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceDetailContent;
